import { useState } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const message = `HAUL IT ALL - Quote Request\n\nName: ${formData.name}\nEmail: ${formData.email}\nPhone: ${formData.phone}\nService: ${formData.service}\n\nMessage:\n${formData.message}`;

      const smsUrl = `sms:9513509072?body=${encodeURIComponent(message)}`;
      window.open(smsUrl, '_self');

      setSubmitStatus('success');
      setFormData({ name: '', email: '', phone: '', service: '', message: '' });
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="contact" className="py-24 bg-gradient-to-b from-gray-900 to-black relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'linear-gradient(rgba(6, 182, 212, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(6, 182, 212, 0.1) 1px, transparent 1px)',
          backgroundSize: '80px 80px'
        }}></div>
      </div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Left Side */}
          <div>
            <span className="inline-flex items-center gap-2 text-cyan-400 text-sm font-bold tracking-[0.3em] uppercase mb-4" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
              <span className="w-8 h-px bg-cyan-400"></span>
              Contact Us
            </span>
            
            <h2 
              className="text-4xl md:text-5xl font-black text-white mb-6"
              style={{ fontFamily: 'Orbitron, sans-serif' }}
            >
              GET IN <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">TOUCH</span>
            </h2>

            <p className="text-xl text-gray-400 mb-8" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
              Ready for a quote? We respond fast!
            </p>

            {/* Phone CTA */}
            <a
              href="tel:9513509072"
              className="group flex items-center gap-4 mb-10 cursor-pointer"
            >
              <div className="w-16 h-16 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-cyan-500/30">
                <i className="ri-phone-fill text-white text-2xl"></i>
              </div>
              <div>
                <p className="text-gray-400 text-sm" style={{ fontFamily: 'Rajdhani, sans-serif' }}>Call Now</p>
                <p className="text-3xl font-black text-white group-hover:text-cyan-400 transition-colors" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                  (951) 350-9072
                </p>
              </div>
            </a>

            {/* Photo Upload Prompt */}
            <div className="bg-gray-800/30 border-2 border-dashed border-gray-600 hover:border-cyan-500/50 rounded-lg p-8 text-center mb-8 transition-colors cursor-pointer">
              <div className="w-16 h-16 bg-gray-700 rounded-lg flex items-center justify-center mx-auto mb-4">
                <i className="ri-image-add-line text-3xl text-cyan-400"></i>
              </div>
              <p className="text-white font-bold mb-2" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                Send Photos for Instant Quote
              </p>
              <p className="text-gray-500 text-sm">
                Text or email us photos of items to haul
              </p>
            </div>

            {/* Map */}
            <div className="bg-gray-800/30 border border-gray-700 rounded-lg p-4">
              <h3 className="text-lg font-bold text-white mb-4" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                <i className="ri-map-pin-line text-cyan-400 mr-2"></i>
                Service Areas
              </h3>
              <div className="w-full h-48 rounded-lg overflow-hidden">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d424552.8!2d-117.35!3d33.55!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80dc6e0e0e0e0e0e%3A0x0!2sRiverside%20County%2C%20CA!5e0!3m2!1sen!2sus!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0, filter: 'grayscale(100%) invert(90%) contrast(90%)' }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Service Area Map"
                ></iframe>
              </div>
              <p className="text-gray-400 text-sm mt-3">
                Proudly serving all of Riverside County
              </p>
            </div>
          </div>

          {/* Right Side - Form */}
          <div className="bg-gray-800/30 border border-gray-700 rounded-lg p-8">
            <h3 className="text-2xl font-bold text-white mb-6" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
              Request a Quote
            </h3>

            <form onSubmit={handleSubmit} className="space-y-5" data-readdy-form id="contact-form">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-400 mb-2" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                  Your Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-gray-900/50 border border-gray-700 rounded-md text-white placeholder-gray-500 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none transition-all text-sm"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-400 mb-2" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-gray-900/50 border border-gray-700 rounded-md text-white placeholder-gray-500 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none transition-all text-sm"
                  placeholder="john@example.com"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-400 mb-2" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-gray-900/50 border border-gray-700 rounded-md text-white placeholder-gray-500 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none transition-all text-sm"
                  placeholder="(951) 234-5678"
                />
              </div>

              <div>
                <label htmlFor="service" className="block text-sm font-medium text-gray-400 mb-2" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                  Service Needed *
                </label>
                <select
                  id="service"
                  name="service"
                  required
                  value={formData.service}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-gray-900/50 border border-gray-700 rounded-md text-white focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none transition-all cursor-pointer text-sm"
                >
                  <option value="">Select a service</option>
                  <option value="Furniture Removal">Furniture Removal</option>
                  <option value="Appliance Removal">Appliance Removal</option>
                  <option value="Garage Cleanout">Garage Cleanout</option>
                  <option value="Storage Unit Cleanout">Storage Unit Cleanout</option>
                  <option value="Eviction Cleanout">Eviction Cleanout</option>
                  <option value="Foreclosure Cleanout">Foreclosure Cleanout</option>
                  <option value="Curbside Pickup">Curbside Pickup</option>
                  <option value="Donation Pickup">Donation Pickup</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-400 mb-2" style={{ fontFamily: 'Rajdhani, sans-serif' }}>
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={4}
                  maxLength={500}
                  value={formData.message}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-gray-900/50 border border-gray-700 rounded-md text-white placeholder-gray-500 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none transition-all resize-none text-sm"
                  placeholder="Tell us about your hauling needs..."
                ></textarea>
                <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</p>
              </div>

              {submitStatus === 'success' && (
                <div className="bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 px-4 py-3 rounded-md">
                  <p className="font-medium text-sm">Message sent successfully! We&apos;ll get back to you soon.</p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-md">
                  <p className="font-medium text-sm">Something went wrong. Please try again or call us directly.</p>
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white py-4 rounded-md text-lg font-bold tracking-wider hover:from-cyan-400 hover:to-blue-500 transition-all duration-300 shadow-lg shadow-cyan-500/30 hover:shadow-cyan-500/50 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                style={{ fontFamily: 'Rajdhani, sans-serif' }}
              >
                {isSubmitting ? 'SENDING...' : 'SEND MESSAGE'}
                <i className="ri-send-plane-fill"></i>
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
